/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState } from 'react';
import { mockCategories, mockOrders, mockProducts, mockSettings } from '../data/mockData';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';

const ShopContext = createContext(null);

const CART_KEY = 'fournitures_cart';
// Production-safe default: never use mock data unless developer changes code manually.
const USE_MOCK_DATA = false;

const readStorage = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage errors in fallback mode
  }
};

const createTrackingCode = () => `FRN-${Date.now().toString().slice(-6)}`;

export const ShopProvider = ({ children }) => {
  const [products, setProducts] = useState(USE_MOCK_DATA ? mockProducts : []);
  const [categories, setCategories] = useState(USE_MOCK_DATA ? mockCategories : []);
  const [settings, setSettings] = useState(mockSettings);
  const [cart, setCart] = useState(() => readStorage(CART_KEY, []));
  const [orders, setOrders] = useState(USE_MOCK_DATA ? mockOrders : []);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let loadedSources = 0;
    const markLoaded = (source) => {
      loadedSources += 1;
      console.log(`[ShopContext] Loaded: ${source} (${loadedSources}/4)`);
      if (loadedSources >= 4) {
        setLoading(false);
        console.log('[ShopContext] All data loaded!');
      }
    };

    const unsubscribeProducts = onSnapshot(
      collection(db, 'products'),
      (snapshot) => {
        if (snapshot.empty) {
          setProducts(USE_MOCK_DATA ? mockProducts : []);
        } else {
          setProducts(snapshot.docs.map((item) => {
            const data = item.data();
            // Use Firestore document ID, ignore any stored 'id' field
            const { id: _, ...cleanData } = data;
            return { id: item.id, ...cleanData };
          }));
        }
        markLoaded('products');
      },
      (error) => {
        console.error('[ShopContext] Products error:', error);
        setProducts(USE_MOCK_DATA ? mockProducts : []);
        markLoaded('products-error');
      }
    );

    const unsubscribeCategories = onSnapshot(
      collection(db, 'categories'),
      (snapshot) => {
        if (snapshot.empty) {
          setCategories(USE_MOCK_DATA ? mockCategories : []);
        } else {
          setCategories(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        }
        markLoaded('categories');
      },
      (error) => {
        console.error('[ShopContext] Categories error:', error);
        setCategories(USE_MOCK_DATA ? mockCategories : []);
        markLoaded('categories-error');
      }
    );

    const unsubscribeSettings = onSnapshot(
      doc(db, 'settings', 'store'),
      (snapshot) => {
        const remoteSettings = snapshot.exists() ? snapshot.data() : {};
        setSettings({
          ...mockSettings,
          ...remoteSettings,
          socialLinks: {
            ...mockSettings.socialLinks,
            ...(remoteSettings?.socialLinks || {})
          }
        });
        markLoaded('settings');
      },
      (error) => {
        console.error('[ShopContext] Settings error:', error);
        setSettings(mockSettings);
        markLoaded('settings-error');
      }
    );

    const unsubscribeOrders = onSnapshot(
      query(collection(db, 'orders'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        if (snapshot.empty) {
          setOrders(USE_MOCK_DATA ? mockOrders : []);
        } else {
          setOrders(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        }
        markLoaded('orders');
      },
      (error) => {
        console.error('[ShopContext] Orders error:', error);
        setOrders(USE_MOCK_DATA ? mockOrders : []);
        markLoaded('orders-error');
      }
    );

    return () => {
      unsubscribeProducts();
      unsubscribeCategories();
      unsubscribeSettings();
      unsubscribeOrders();
    };
  }, []);

  useEffect(() => {
    if (!loading) writeStorage(CART_KEY, cart);
  }, [cart, loading]);

  const addToCart = (product) => {
    setCart((current) => {
      const existing = current.find((item) => item.id === product.id);
      if (existing) {
        return current.map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }

      return [
        ...current,
        {
          id: product.id,
          name: product.name,
          price: Number(product.price) || 0,
          image: product.image || '',
          images: product.images || [],
          quantity: 1
        }
      ];
    });
  };

  const updateCartQuantity = (productId, nextQuantity) => {
    if (nextQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCart((current) =>
      current.map((item) => (item.id === productId ? { ...item, quantity: nextQuantity } : item))
    );
  };

  const removeFromCart = (productId) => {
    setCart((current) => current.filter((item) => item.id !== productId));
  };

  const clearCart = () => setCart([]);

  const getCartCount = () => cart.reduce((sum, item) => sum + item.quantity, 0);

  const getCartTotal = () =>
    cart.reduce((sum, item) => sum + (Number(item.price) || 0) * item.quantity, 0);

  const placeOrder = async (orderData) => {
    const trackingCode = createTrackingCode();

    const newOrder = {
      trackingCode,
      status: 'pending',
      createdAt: new Date().toISOString(),
      ...orderData
    };

    await addDoc(collection(db, 'orders'), newOrder);
    clearCart();
    return trackingCode;
  };

  const addProduct = async (productData) => {
    const { id: _, ...dataWithoutId } = productData;
    const newProduct = {
      rating: 0,
      reviews: 0,
      originalPrice: dataWithoutId.originalPrice || null,
      images: dataWithoutId.images?.length ? dataWithoutId.images : [dataWithoutId.image].filter(Boolean),
      ...dataWithoutId,
      price: Number(dataWithoutId.price) || 0,
      stock: Number(dataWithoutId.stock) || 0
    };

    await addDoc(collection(db, 'products'), newProduct);
    return newProduct;
  };

  const updateProduct = async (id, productData) => {
    const { id: _, ...dataWithoutId } = productData;
    const nextPayload = {
      ...dataWithoutId,
      price: Number(dataWithoutId.price) || 0,
      stock: Number(dataWithoutId.stock) || 0,
      images: dataWithoutId.images?.length ? dataWithoutId.images : [dataWithoutId.image].filter(Boolean)
    };
    await updateDoc(doc(db, 'products', id), nextPayload);
  };

  const deleteProduct = async (id) => {
    await deleteDoc(doc(db, 'products', id));
  };

  const addCategory = async (categoryData) => {
    const newCategory = {
      id:
        categoryData.id ||
        categoryData.name
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '-'),
      name: categoryData.name,
      nameEn: categoryData.nameEn || ''
    };

    const exists = categories.some(
      (item) => item.id === newCategory.id || item.name.trim() === newCategory.name.trim()
    );
    if (exists) return;
    await setDoc(doc(db, 'categories', newCategory.id), newCategory);
  };

  const updateCategory = async (id, categoryData) => {
    await updateDoc(doc(db, 'categories', id), {
      name: categoryData.name,
      nameEn: categoryData.nameEn || ''
    });
  };

  const deleteCategory = async (categoryId) => {
    await deleteDoc(doc(db, 'categories', categoryId));
  };

  const updateSettings = async (nextSettings) => {
    await setDoc(doc(db, 'settings', 'store'), nextSettings, { merge: true });
  };

  const updateOrderStatus = async (orderId, status) => {
    await updateDoc(doc(db, 'orders', orderId), { status });
  };

  const deleteOrder = async (orderId) => {
    await deleteDoc(doc(db, 'orders', orderId));
  };

  const value = {
    products,
    categories,
    settings,
    cart,
    orders,
    loading,
    addToCart,
    updateCartQuantity,
    removeFromCart,
    clearCart,
    getCartCount,
    getCartTotal,
    placeOrder,
    addProduct,
    updateProduct,
    deleteProduct,
    addCategory,
    updateCategory,
    deleteCategory,
    updateSettings,
    updateOrderStatus,
    deleteOrder
  };

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
};

export const useShop = () => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
};

export default ShopContext;
